import { Component } from 'react';
import { logAppError } from '../common/util/errorLogger';

// A chunk fails to load when the app is left open across a new deploy: the
// stale index.html / running bundle references chunk filenames whose content
// hash changed and no longer exist on the server. A fresh reload pulls the
// current index.html and chunk names, resolving it.
const CHUNK_RELOAD_KEY = 'chunk_reload_attempt';
const CHUNK_RELOAD_WINDOW_MS = 20000;

export const isChunkLoadError = (error) => {
  const name = error?.name || '';
  const msg = String(error?.message || error || '');
  return (
    name === 'ChunkLoadError' ||
    /Loading chunk [\w-]+ failed/i.test(msg) ||
    /Loading CSS chunk [\w-]+ failed/i.test(msg) ||
    // Dynamic import() failures across browsers
    /failed to fetch dynamically imported module/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg) ||
    /importing a module script failed/i.test(msg) ||
    // A missing chunk is served the SPA index.html (HTTP 200, text/html) by
    // Firebase Hosting's catch-all rewrite. The browser then refuses to run
    // HTML as JS. These are the browser-specific messages for that case:
    //   Chrome/Edge: "Failed to load module script: Expected a JavaScript
    //     module script but the server responded with a MIME type of
    //     'text/html'..."
    //   Safari:      "Unexpected token '<'" / "'text/html' is not a valid
    //     JavaScript MIME type."
    //   Firefox:     "expected expression, got '<'"
    /Expected a JavaScript(?:-or-Wasm)? module script/i.test(msg) ||
    /is not a valid JavaScript MIME type/i.test(msg) ||
    /MIME type (?:\(['"]?text\/html['"]?\)|of ['"]?text\/html['"]?)/i.test(
      msg,
    ) ||
    /Unexpected token['"\s]*</i.test(msg) ||
    /expected expression, got ['"\s]*</i.test(msg)
  );
};

class ErrorBoundary extends Component {
  state = {
    hasError: false,
    error: null,
    isChunkError: false,
    isReloading: false,
  };

  static getDerivedStateFromError(error) {
    const isChunkError = isChunkLoadError(error);
    return {
      hasError: true,
      error,
      isChunkError,
      // For a stale-deploy chunk error we auto-reload, so optimistically show
      // the calm "Updating portal…" screen instead of a crash card. If the
      // reload turns out to be loop-guarded, componentDidCatch flips this off
      // to reveal the actionable prompt.
      isReloading: isChunkError,
    };
  }

  componentDidCatch(error, errorInfo) {
    if (isChunkLoadError(error)) {
      // A new version was deployed. Reload once to fetch fresh assets, but
      // guard against an infinite loop if the chunk is genuinely gone.
      let lastAttempt = 0;
      try {
        lastAttempt = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY)) || 0;
      } catch (_) {}
      if (Date.now() - lastAttempt > CHUNK_RELOAD_WINDOW_MS) {
        try {
          sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
        } catch (_) {}
        window.location.reload();
      } else {
        // Recently reloaded and it happened again — stop auto-reloading and
        // show the friendly, actionable prompt instead of looping.
        this.setState({ isReloading: false });
      }
      return;
    }
    console.error('ErrorBoundary caught:', error, errorInfo);
    logAppError({ error, errorInfo, source: 'ErrorBoundary' });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/home';
  };

  render() {
    // Stale-deploy chunk error: we're auto-reloading momentarily. Show a calm
    // "Updating portal…" screen instead of flashing the crash card.
    if (
      this.state.hasError &&
      this.state.isChunkError &&
      this.state.isReloading
    ) {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            padding: '2em',
            textAlign: 'center',
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            backgroundColor: '#f9fafb',
            color: '#4183c4',
          }}
        >
          <style>{`@keyframes portalSpin { to { transform: rotate(360deg); } }`}</style>
          <div
            style={{
              width: '42px',
              height: '42px',
              border: '4px solid #d9e6f2',
              borderTopColor: '#4183c4',
              borderRadius: '50%',
              animation: 'portalSpin 0.8s linear infinite',
              marginBottom: '1.1em',
            }}
          />
          <h2 style={{ margin: 0, color: '#1a1a1a', fontWeight: 600 }}>
            Updating portal…
          </h2>
          <p
            style={{
              color: '#666',
              fontSize: '0.95em',
              marginTop: '0.5em',
            }}
          >
            Loading the latest version. This only takes a moment.
          </p>
        </div>
      );
    }

    if (this.state.hasError) {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            padding: '2em',
            textAlign: 'center',
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            backgroundColor: '#f9fafb',
          }}
        >
          <div
            style={{
              backgroundColor: '#fff',
              borderRadius: '12px',
              padding: '2.5em',
              maxWidth: '420px',
              width: '100%',
              boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            }}
          >
            <div style={{ fontSize: '3em', marginBottom: '0.3em' }}>
              {this.state.isChunkError ? '🔄' : '⚠️'}
            </div>
            <h2 style={{ margin: '0 0 0.5em', color: '#1a1a1a' }}>
              {this.state.isChunkError
                ? 'A new version is available'
                : 'Something went wrong'}
            </h2>
            <p style={{ color: '#666', fontSize: '0.95em', lineHeight: 1.5 }}>
              {this.state.isChunkError
                ? 'The app was updated since you opened it. Reload to get the latest version.'
                : 'The app ran into an unexpected error. This has been logged. Try reloading the page or going back to the dashboard.'}
            </p>
            <div
              style={{
                display: 'flex',
                gap: '0.75em',
                justifyContent: 'center',
                marginTop: '1.5em',
                flexWrap: 'wrap',
              }}
            >
              <button
                onClick={this.handleReload}
                style={{
                  padding: '0.65em 1.5em',
                  fontSize: '0.95em',
                  fontWeight: 600,
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: '#4183c4',
                  color: '#fff',
                  cursor: 'pointer',
                }}
              >
                Reload Page
              </button>
              <button
                onClick={this.handleGoHome}
                style={{
                  padding: '0.65em 1.5em',
                  fontSize: '0.95em',
                  fontWeight: 600,
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  backgroundColor: '#fff',
                  color: '#333',
                  cursor: 'pointer',
                }}
              >
                Go to Dashboard
              </button>
            </div>
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <pre
                style={{
                  marginTop: '1.5em',
                  padding: '1em',
                  backgroundColor: '#fef2f2',
                  borderRadius: '6px',
                  fontSize: '0.75em',
                  textAlign: 'left',
                  overflow: 'auto',
                  maxHeight: '200px',
                  color: '#dc2626',
                }}
              >
                {this.state.error.toString()}
              </pre>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
